import { EditConfigurationValidationWarningsModal } from 'components/Modals/EditConfigurationValidationWarningsModal/EditConfigurationValidationWarningsModal';
import ConfigureForm from './ConfigureForm/ConfigureForm';
import {
  ByCodeflexText,
  ByCodeflexWrapper,
  Container,
  NoModelSelectedLabel,
  NoModelSelectedWrapper,
  Row,
  Wrapper,
} from './ConfigurePage.styled';
import FormImages from './FormImages/FormImages';
import { ReactComponent as CodeflexLogo } from 'assets/icons/codeflexLogoTextRight.svg';
import { useTranslation } from 'react-i18next';
import {
  getModelIdFromPath,
  useGetBuilderEditVersionId,
} from 'providers/QueryProvider/hooks';
import Button from 'components/Button/Button';
import BackButton from 'components/BackButton/BackButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { IImageSection, useFilterOutHiddenImages } from './FormImages/hooks';
import { useHasImages } from 'utils/hooks/useHasImages';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import { RoutesConfig } from 'navigation/routes';
import { AppMode } from 'store/Common/reducers/common';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import { EditTestConfigurationModal } from 'components/Modals/EditTestConfigurationModal/EditTestConfigurationModal';
import { useQuotationLineResolveParams } from 'utils/hooks/useQuotationLineResolveParams';
import { useTemplateConfigureParams } from 'utils/hooks/useTemplateConfigureParams';
import { isOpenedFromHostApp } from 'utils/hostApp';

const ConfigurePage = () => {
  const { t } = useTranslation();
  const modelId = getModelIdFromPath();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const taxRate = searchParams.get('taxRate');
  const navigate = useNavigate();
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );
  const images = useSelector(
    (state: IRootReducerState) => state.modelInfo.images
  );
  const visibleImages: IImageSection[] = useFilterOutHiddenImages(images);

  // Used for toggling between 2 form styles
  const hasImages = useHasImages(!!visibleImages.length);

  const general = useSelector(
    (state: IRootReducerState) => state.modelInfo.general
  );
  usePageTitle(general ? general.name : 'Configure', general ? false : true);

  const editVersionId: number | undefined = useGetBuilderEditVersionId();
  const quotationLineResolveParams = useQuotationLineResolveParams();
  const isTemplateMode = !!useTemplateConfigureParams();
  // Opened in a tab by another app purely to build a template: there is nothing
  // to go back to in this tab, and the dealer panel Back leads somewhere the
  // user may have no access to. The tab closes itself once the template saves.
  const isStandaloneTemplateTab = isTemplateMode && isOpenedFromHostApp();

  const handleBackOnModelEditor = () => {
    if (editVersionId) {
      navigate(
        `${RoutesConfig.Builder.fullPath.replace(
          ':id',
          editVersionId.toString()
        )}`
      );
    }
  };

  const getBackTarget = (): { destination?: string; onClick: () => void } => {
    // Building a template: back to the dealer panel model list it was opened
    // from. location.state carries the supplier context that page needs.
    if (isTemplateMode) {
      return {
        destination: t('Models'),
        onClick: () =>
          navigate(RoutesConfig.DealerPanelModels.fullPath, {
            state: location.state,
          }),
      };
    }

    // Resolving a quotation line: go back to the quotation it belongs to
    if (quotationLineResolveParams) {
      return {
        destination: t('Quotation'),
        onClick: () =>
          navigate(
            RoutesConfig.BuyerSingleQuotation.fullPath.replace(
              ':id',
              quotationLineResolveParams.quotationId
            )
          ),
      };
    }

    // Check taxRate condition first
    if (taxRate != null) {
      return {
        destination: t('Suppliers'),
        onClick: () => navigate(RoutesConfig.Suppliers.fullPath),
      };
    }

    // Then check appMode
    switch (appMode) {
      case AppMode.BUYER:
        return {
          destination: t('New order'),
          onClick: () => navigate(RoutesConfig.BuyerNewOrder.fullPath),
        };
      case AppMode.SUPPLIER:
        return {
          destination: t('New order'),
          onClick: () => navigate(RoutesConfig.SupplierNewOrder.fullPath),
        };
      case AppMode.DEALER_PANEL:
        return {
          destination: t('Suppliers'),
          onClick: () => navigate(RoutesConfig.Suppliers.fullPath),
        };
      default:
        return { onClick: () => navigate(-1) }; // Navigate to -1 instead of direct route in order to preserve IDealerPanelSupplierDataFromNavigationState
    }
  };

  const backTarget = getBackTarget();

  return (
    <>
      {!modelId ? (
        <NoModelSelectedWrapper>
          <NoModelSelectedLabel>
            {t('No model is selected')}
          </NoModelSelectedLabel>
          {appMode === AppMode.BUYER ? (
            <Button
              label={t('Back to models page')}
              onClick={() => navigate(RoutesConfig.CustomModels.fullPath)}
              primary
            />
          ) : null}
        </NoModelSelectedWrapper>
      ) : (
        <Wrapper>
          {editVersionId && (
            <BackButton
              destination={t('Model Editor')}
              onClick={handleBackOnModelEditor}
            />
          )}
          {[AppMode.BUYER, AppMode.DEALER_PANEL].includes(appMode) &&
          !editVersionId &&
          !isStandaloneTemplateTab ? (
            <BackButton
              destination={backTarget.destination}
              onClick={backTarget.onClick}
            />
          ) : null}
          <Container hasImages={hasImages}>
            <Row>
              <FormImages visibleImages={visibleImages} />
              <ConfigureForm hasImages={hasImages} />
            </Row>
            <ByCodeflexWrapper>
              <ByCodeflexText>{t('Powered by')}:</ByCodeflexText>
              <Icon svg={CodeflexLogo} size={123} fill={COLORS.BLACK} />
            </ByCodeflexWrapper>
            <EditConfigurationValidationWarningsModal />
          </Container>
        </Wrapper>
      )}
      {isTemplateMode ? null : <EditTestConfigurationModal />}
    </>
  );
};

export default ConfigurePage;
